import * as THREE from 'three';
import { genToThree } from './coords';
import type { FlyCamera } from './flyCamera';
import type { GpuTimer } from './gpuTimer';
import type { TileManager } from './tileManager';
import type { WorkerPool } from './workerPool';

/** Benchmark segments: seconds each, in order. */
const SEGMENTS = [
  { name: 'street', seconds: 20 },
  { name: 'orbit', seconds: 20 },
  { name: 'skyline', seconds: 20 },
] as const;

interface Sample {
  seg: number;
  frameMs: number;
  gpuMs: number | null;
  calls: number;
  triangles: number;
}

/**
 * `?bench=1`: a fixed 60 s flythrough once the start area has loaded, then a report of frame and GPU
 * times (average, p95, max), draw calls and triangles per segment, in an overlay with the text ready
 * to copy (also `window.__bench`). Segments:
 * - street: 2.5 m above the road, following the straightest chain of road edges from the origin;
 * - orbit: a 180 m circle 70 m up around the street start;
 * - skyline: a straight pass 150 m up across the city, looking ahead and down.
 */
export class Benchmark {
  private phase: 'loading' | 'running' | 'done' = 'loading';
  private readonly t0 = performance.now();
  private idleSince = 0;
  private start = 0;
  private readonly samples: Sample[] = [];
  private street: THREE.Vector3[] = [];
  private streetLen = 0;
  private readonly ray = new THREE.Raycaster();

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    private readonly fly: FlyCamera,
    private readonly tiles: TileManager,
    private readonly pool: WorkerPool,
    private readonly gpu: GpuTimer,
    private readonly container: HTMLElement,
  ) {
    fly.enabled = false;
  }

  get running(): boolean {
    return this.phase !== 'done';
  }

  /** Before the frame is rendered: place the camera. */
  update(now: number): void {
    if (this.phase === 'loading') {
      const busy = this.pool.queueDepth + this.pool.inFlight + this.tiles.applyQueueLength > 0;
      if (busy) this.idleSince = 0;
      else if (!this.idleSince) this.idleSince = now;
      const ready = (this.idleSince && now - this.idleSince > 2000) || now - this.t0 > 30000;
      if (ready && this.buildStreet()) {
        this.phase = 'running';
        this.start = now;
      } else return;
    }
    if (this.phase !== 'running') return;
    const t = (now - this.start) / 1000;
    let seg = 0;
    let acc = 0;
    while (seg < SEGMENTS.length && t >= acc + SEGMENTS[seg].seconds) acc += SEGMENTS[seg++].seconds;
    if (seg >= SEGMENTS.length) {
      this.finish();
      return;
    }
    const u = (t - acc) / SEGMENTS[seg].seconds;
    const [eye, at] = this.pose(seg, u);
    this.camera.position.copy(eye);
    this.fly.lookAt(at);
  }

  /** After the frame is rendered: record it. */
  record(frameMs: number, info: { calls: number; triangles: number }): void {
    if (this.phase !== 'running') return;
    const t = (performance.now() - this.start) / 1000;
    let seg = 0;
    let acc = 0;
    while (seg < SEGMENTS.length - 1 && t >= acc + SEGMENTS[seg].seconds) acc += SEGMENTS[seg++].seconds;
    this.samples.push({ seg, frameMs, gpuMs: this.gpu.last, calls: info.calls, triangles: info.triangles });
  }

  private pose(seg: number, u: number): [THREE.Vector3, THREE.Vector3] {
    const s0 = this.street[0];
    if (seg === 0) {
      const eye = this.along(u * this.streetLen);
      const at = this.along(Math.min(this.streetLen, u * this.streetLen + 25));
      return [eye, at.clone().setY(eye.y - 0.5)];
    }
    if (seg === 1) {
      const a = u * Math.PI * 2;
      return [new THREE.Vector3(s0.x + Math.cos(a) * 180, s0.y + 70, s0.z + Math.sin(a) * 180), s0.clone()];
    }
    const from = new THREE.Vector3(s0.x - 600, s0.y + 150, s0.z + 300);
    const to = new THREE.Vector3(s0.x + 600, s0.y + 150, s0.z - 300);
    const eye = from.clone().lerp(to, u);
    return [eye, eye.clone().add(to.clone().sub(from).normalize().multiplyScalar(300)).setY(s0.y)];
  }

  private along(d: number): THREE.Vector3 {
    let acc = 0;
    for (let i = 1; i < this.street.length; i++) {
      const l = this.street[i].distanceTo(this.street[i - 1]);
      if (acc + l >= d) return this.street[i - 1].clone().lerp(this.street[i], (d - acc) / l);
      acc += l;
    }
    return this.street[this.street.length - 1].clone();
  }

  /** The straightest chain of road edges from the node nearest the origin, ~300 m, 2.5 m above the road. */
  private buildStreet(): boolean {
    const t = this.tiles.tiles.get('0,0') ?? [...this.tiles.tiles.values()].find((x) => x.tx === 0 && x.ty === 0);
    const g = t?.roads?.graph;
    if (!g || g.edges.length === 0) return false;
    const n = g.nodes.length / 2;
    const adj: number[][] = Array.from({ length: n }, () => []);
    for (let e = 0; e < g.edges.length; e += 2) {
      adj[g.edges[e]].push(g.edges[e + 1]);
      adj[g.edges[e + 1]].push(g.edges[e]);
    }
    const P = (i: number) => new THREE.Vector2(g.nodes[i * 2], g.nodes[i * 2 + 1]);
    let cur = 0;
    for (let i = 1; i < n; i++) if (adj[i].length && P(i).lengthSq() < P(cur).lengthSq()) cur = i;
    const chain = [cur];
    let prev = -1;
    let len = 0;
    while (len < 30000 && chain.length < 40) {
      const here = P(cur);
      const dirIn = prev >= 0 ? here.clone().sub(P(prev)).normalize() : null;
      let best = -1;
      let bestDot = -2;
      for (const nb of adj[cur]) {
        if (nb === prev || chain.includes(nb)) continue;
        const d = dirIn ? P(nb).sub(here).normalize().dot(dirIn) : 1;
        if (d > bestDot) {
          bestDot = d;
          best = nb;
        }
      }
      if (best < 0) break;
      len += P(best).distanceTo(here);
      prev = cur;
      cur = best;
      chain.push(cur);
    }
    if (chain.length < 2) return false;
    const roads: THREE.Object3D[] = [];
    for (const tile of this.tiles.tiles.values()) for (const c of tile.city?.children ?? []) if (c.name === 'roads') roads.push(c);
    this.street = chain.map((i) => {
      const p = P(i);
      const v = genToThree(p.x, p.y, 0);
      this.ray.set(new THREE.Vector3(v.x, 2000, v.z), new THREE.Vector3(0, -1, 0));
      const hit = this.ray.intersectObjects(roads, false)[0];
      return v.setY((hit ? hit.point.y : 0) + 2.5);
    });
    this.streetLen = this.street.reduce((s, p, i) => (i ? s + p.distanceTo(this.street[i - 1]) : 0), 0);
    return this.streetLen > 50;
  }

  private finish(): void {
    this.phase = 'done';
    this.fly.enabled = true;
    const stat = (xs: number[]) => {
      if (xs.length === 0) return { avg: NaN, p95: NaN, max: NaN };
      const s = [...xs].sort((a, b) => a - b);
      return { avg: s.reduce((a, b) => a + b, 0) / s.length, p95: s[Math.min(s.length - 1, Math.floor(s.length * 0.95))], max: s[s.length - 1] };
    };
    const f = (x: number) => (Number.isFinite(x) ? x.toFixed(1) : '–');
    const lines = [`bench ${new Date().toISOString()} ${navigator.userAgent.match(/(Chrome|Firefox|Safari)\/[\d.]+/)?.[0] ?? ''}`, `canvas ${this.container.clientWidth}×${this.container.clientHeight} @${window.devicePixelRatio} · ${location.search}`];
    const rows = [...SEGMENTS.map((s, i) => ({ name: s.name, xs: this.samples.filter((x) => x.seg === i) })), { name: 'all', xs: this.samples }];
    for (const r of rows) {
      const fr = stat(r.xs.map((x) => x.frameMs));
      const gp = stat(r.xs.filter((x) => x.gpuMs !== null).map((x) => x.gpuMs!));
      const calls = stat(r.xs.map((x) => x.calls));
      const tris = stat(r.xs.map((x) => x.triangles / 1e6));
      lines.push(`${r.name.padEnd(8)} frame avg ${f(fr.avg)} p95 ${f(fr.p95)} max ${f(fr.max)} ms (${f(1000 / fr.avg)} fps) · gpu avg ${f(gp.avg)} p95 ${f(gp.p95)} ms · draws ${f(calls.avg)} (max ${f(calls.max)}) · tris ${tris.avg.toFixed(2)} M (max ${tris.max.toFixed(2)})`);
    }
    const text = lines.join('\n');
    console.log(text);
    (window as unknown as { __bench: string }).__bench = text;
    const box = document.createElement('div');
    box.className = 'bench-report';
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.readOnly = true;
    const copy = document.createElement('button');
    copy.textContent = 'Copy';
    copy.onclick = () => void navigator.clipboard?.writeText(text);
    const close = document.createElement('button');
    close.textContent = 'Close';
    close.onclick = () => box.remove();
    box.append(ta, copy, close);
    this.container.appendChild(box);
  }
}
